/**
 * Teacher → Live Classes (host-side).
 *
 * Lists live classrooms where the teacher is host. Each row has a
 * "Transcribe" button that uploads an audio recording, runs Whisper, and
 * shows the transcript with segment timestamps (Phase-5).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Video, Loader2, ExternalLink, Plus, Captions, Upload, FileText,
} from 'lucide-react';
import { portalApi } from './teacherApi';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try {
    const pt = sessionStorage.getItem('school_preview_token');
    if (pt) return pt;
  } catch { /* noop */ }
  return getToken();
};

const STATUS_COLOR = {
  scheduled: 'bg-sky-100 text-sky-700',
  live:      'bg-emerald-100 text-emerald-700',
  ended:     'bg-slate-100 text-slate-500',
  completed: 'bg-slate-100 text-slate-500',
};

const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

const fmtSec = (s) => {
  s = Number(s) || 0;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

// =================== Transcription Dialog ===================
const TranscribeDialog = ({ open, onOpenChange, liveClass }) => {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);     // {job_id, status, error}
  const [transcript, setTranscript] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null); setJob(null); setTranscript(null); setBusy(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    // Load existing transcript if any
    if (liveClass?.id) {
      fetch(`${API_URL}/api/school/lms/live-classes/${liveClass.id}/transcript`, {
        headers: { Authorization: `Bearer ${_token()}` },
      }).then(r => r.json()).then(d => {
        if (d.has_transcript) setTranscript(d);
      }).catch(() => { /* ignore */ });
    }
  }, [open, liveClass?.id]);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setJob(null);
    try {
      const fd = new FormData();
      fd.append('audio', file, file.name);
      const res = await fetch(`${API_URL}/api/school/lms/live-classes/${liveClass.id}/transcript`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${_token()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const j = await res.json();
      setJob(j);
      // poll job status every 2s
      pollRef.current = setInterval(async () => {
        try {
          const jr = await fetch(`${API_URL}/api/school/lms/live-classes/${liveClass.id}/transcript/job/${j.job_id}`, {
            headers: { Authorization: `Bearer ${_token()}` },
          }).then(r => r.json());
          setJob(jr);
          if (jr.status === 'done' || jr.status === 'failed') {
            clearInterval(pollRef.current); pollRef.current = null;
            setBusy(false);
            if (jr.status === 'done') {
              const t = await fetch(`${API_URL}/api/school/lms/live-classes/${liveClass.id}/transcript`, {
                headers: { Authorization: `Bearer ${_token()}` },
              }).then(r => r.json());
              if (t.has_transcript) setTranscript(t);
              toast.success('Transcript ready');
            } else {
              toast.error(jr.error || 'Transcription failed');
            }
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setBusy(false);
      toast.error(e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="live-transcribe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Captions className="h-4 w-4 text-indigo-500" />
            Transcribe — {liveClass?.title || 'Live class'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Upload an audio/video file (up to 25 MB). Whisper will transcribe it with
            segment timestamps. Supported formats: mp3, m4a, wav, webm, mp4.
          </p>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef} type="file" accept="audio/*,video/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="live-transcribe-file"
            />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                    disabled={busy} data-testid="live-transcribe-pick">
              <Upload className="h-4 w-4 mr-1" /> Pick audio file
            </Button>
            <span className="text-xs text-slate-500 truncate">
              {file ? file.name : 'No file selected'}
            </span>
            <Button size="sm" className="ml-auto" disabled={!file || busy}
                    onClick={upload} data-testid="live-transcribe-start">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Start
            </Button>
          </div>

          {job && (
            <div className="text-xs px-2 py-1 rounded bg-slate-50 border"
                 data-testid={`live-transcribe-job-${job.status}`}>
              Job <code className="text-[11px]">{job.job_id?.slice(-8)}</code> · status:
              <Badge className="ml-2" variant={job.status === 'done' ? 'default' : 'outline'}>
                {job.status}
              </Badge>
              {job.error && <div className="text-rose-600 mt-1">{job.error}</div>}
            </div>
          )}

          {transcript && (
            <div className="border rounded p-3 max-h-80 overflow-auto bg-slate-50"
                 data-testid="live-transcript-output">
              <div className="text-xs text-slate-500 mb-2 flex items-center gap-2 flex-wrap">
                <FileText className="h-3.5 w-3.5" />
                {transcript.language?.toUpperCase()} · {fmtSec(transcript.duration)} ·
                {transcript.segments?.length || 0} segment(s)
              </div>
              {transcript.segments?.length ? (
                <ol className="space-y-1.5">
                  {transcript.segments.map((s, i) => (
                    <li key={i} className="text-sm flex gap-2">
                      <span className="text-xs text-slate-400 tabular-nums w-16 shrink-0">
                        [{fmtSec(s.start)}–{fmtSec(s.end)}]
                      </span>
                      <span className="flex-1">{s.text}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{transcript.text}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =================== Main page ===================
export const TeacherLiveClasses = () => {
  const [transcribing, setTranscribing] = useState(null);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['teacher-live-classes'],
    queryFn: () => portalApi('/teacher/live-classes'),
  });

  return (
    <div className="space-y-3" data-testid="teacher-live-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Video className="h-5 w-5 text-indigo-500" /> Live classes
          <Badge variant="outline">{rows.length}</Badge>
        </h2>
        <Button size="sm" asChild data-testid="teacher-live-new">
          <a href="/live-classrooms"><Plus className="h-4 w-4 mr-1" /> Create / browse all</a>
        </Button>
      </div>

      {isLoading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-slate-500 p-8 border border-dashed rounded">
          You haven&apos;t hosted any live classes yet. Click <strong>Create / browse all</strong> to start one.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.id} data-testid={`teacher-live-${r.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm">{r.title || r.name || 'Live class'}</h3>
                      <Badge className={STATUS_COLOR[r.status] || STATUS_COLOR.scheduled}>
                        {r.status || 'scheduled'}
                      </Badge>
                      {r.meeting_provider && (
                        <Badge variant="outline" className="text-[10px]">{r.meeting_provider}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Starts {fmt(r.starts_at)} · {r.duration_minutes ? `${r.duration_minutes} min` : 'open-ended'}
                    </p>
                    {r.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.description}</p>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline"
                            onClick={() => setTranscribing(r)}
                            data-testid={`teacher-live-transcribe-${r.id}`}>
                      <Captions className="h-3.5 w-3.5 mr-1" /> Transcribe
                    </Button>
                    {r.meeting_url && (
                      <Button size="sm" variant="outline" asChild>
                        <a href={r.meeting_url} target="_blank" rel="noopener noreferrer">
                          Join <ExternalLink className="h-3.5 w-3.5 ml-1" />
                        </a>
                      </Button>
                    )}
                    <Button size="sm" asChild>
                      <a href={`/live-classroom/${r.id}`}>Open</a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TranscribeDialog
        open={!!transcribing}
        onOpenChange={(v) => !v && setTranscribing(null)}
        liveClass={transcribing}
      />
    </div>
  );
};

export default TeacherLiveClasses;
